import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { storeService } from '@/shared/api/services/store.service';
import type { StoreCatalogResponse, StoreProduct } from '@/types/store';

const STORE_KEY = 'store-catalog';

/**
 * Post-pivot storefront catalog (GET /v1/store) — Facade Pack and Pro Plan
 * Pass. Copy + reference price are server-driven and locale-sensitive, so the
 * locale is part of the cache key (mirrors `usePasses`).
 *
 * `projectId` scopes the catalog to a building (pack `project_state`).
 * `enabled` gates the auth-only fetch — paywall dialogs are root-mounted, so
 * an ungated query would 401 on pre-auth pages; callers pass the dialog-open
 * condition.
 */
export function useStoreCatalog({
  projectId,
  enabled = true,
}: {
  projectId?: string;
  enabled?: boolean;
} = {}) {
  const locale = useLocale();
  return useQuery<StoreCatalogResponse>({
    queryKey: [STORE_KEY, locale, projectId ?? null],
    queryFn: () => storeService.getCatalog(projectId),
    enabled,
    staleTime: 5 * 60 * 1000, // catalog copy/price is stable
    gcTime: 30 * 60 * 1000,
  });
}

/** Find the Facade Pack item in a loaded catalog. */
export function findPackProduct(
  catalog: StoreCatalogResponse | undefined
): StoreProduct | undefined {
  return catalog?.data.find((p) => p.attributes.kind === 'pack');
}

/**
 * Find the Pro Plan card in a loaded catalog. Carries the server-computed
 * `is_owned` flag — when true, a contractor activates buildings via a monthly
 * slot instead of buying a one-time pack.
 */
export function findProPlanProduct(
  catalog: StoreCatalogResponse | undefined
): StoreProduct | undefined {
  return catalog?.data.find((p) => p.attributes.kind === 'pro_plan');
}
